import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ListGroup, Modal } from 'react-bootstrap';
import { FileUploader } from 'react-drag-drop-files';
import { useUploadInvoicesMutation } from '../../store/invoices/invoices.api';
import { useAlert } from '../../provider/alert/userAlert';
import { notificationsEnabled } from '../../config/feature-flags';

interface UploadInvoiceModalProps {
   open: boolean;
   onClose: () => void;
}

const fileTypes = ['PDF'];

const UploadInvoiceModal: FC<UploadInvoiceModalProps> = ({open, onClose}) => {
   const { t } = useTranslation(['common', 'notifications']);
   const { setAlert } = useAlert();
   const [files, setFiles] = useState<File[]>([]);
   const [uploadInvoices, { isLoading }] = useUploadInvoicesMutation();
   const notificationOptions = { context: notificationsEnabled ? undefined : 'withoutNotifications' };
   const uploadFailedMessage = t('notifications:uploadFailed', notificationOptions);

   const handleChange = (selectedFiles: File | FileList) => {
      if (selectedFiles instanceof FileList) {
         setFiles((prevFiles) => [...prevFiles, ...Array.from(selectedFiles)]);
      } else {
         setFiles((prevFiles) => [...prevFiles, selectedFiles]);
      }
   };

   const removeFiles = () => {
      setFiles([]);
   };

   const handleSubmit = () => {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));

      uploadInvoices(formData)
         .unwrap()
         .then((attempts) => {
            removeFiles();
            const failed = attempts.filter(attempt => attempt.status === 'FAILED');
            const message = failed.length === 0 ? t('notifications:uploadReceived')
               : failed.length < attempts.length ? t('notifications:uploadPartiallyReceived', notificationOptions)
               : failed.length === 1 ? t(`notifications:issues.${failed[0].issues[0]?.code}`, { defaultValue: uploadFailedMessage })
               : uploadFailedMessage;
            setAlert(failed.length ? 'error' : 'success', message ?? '');
            onClose();
         })
         .catch((error) => {
            setAlert('error', (error?.status === 413 ? t('notifications:uploadTooLarge') : uploadFailedMessage) ?? '');
         });
   };

   return (
      <Modal show={open} onHide={onClose} centered>
         <Modal.Header closeButton>
            <Modal.Title>{t('common:settings.uploadInvoices')}</Modal.Title>
         </Modal.Header>
         <Modal.Body>
            <FileUploader
               handleChange={handleChange}
               name="file"
               types={fileTypes}
               multiple={true}
               label={t('common:settings.uploadOrDropAFile')}
               uploadedLabel={t('common:settings.uploadSuccessUploadAnother')}
               classes="file-uploader"
            />
            <div className="support-note">
               {t('common:settings.supportOnly')}
            </div>

            {files.length > 0 && (
               <ListGroup className="mt-2 list-size">
                  {files.map((file: File, index: number) => (
                     <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                        {file.name}
                        <span className="badge bg-primary">
                              {(file.size / 1024).toFixed(2)} KB
                           </span>
                     </ListGroup.Item>
                  ))}
               </ListGroup>
            )}
         </Modal.Body>
         <Modal.Footer>
            <Button variant="outline-secondary" onClick={removeFiles} className="custom-btn-width">
               {t('common:common.remove')}
            </Button>
            <Button onClick={handleSubmit} className="custom-btn-width primary" disabled={files.length === 0 || isLoading}>
               {t('common:common.save')}
            </Button>
         </Modal.Footer>
      </Modal>
   );
};

export default UploadInvoiceModal;
